<script setup lang="ts">
import type { BookV1 } from '@ilteducation/content-registry-types';
import BookCover from '@/components/bookshelf/BookCover.vue';
import BookPreview from '@/components/bookshelf/BookPreview.vue';
import { RelatedContent } from '@ilteducation/similar-content/src/use-similar-contents';
import { computed, ref, watch } from 'vue';
import useSimilarContentContext from '@/composables/use-similar-content-context';
import { RelatedContentItem } from '@ilteducation/similar-content';
import { createSearchId, useSearchContexts } from '@ilteducation/content-search';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useCategories } from '@ilteducation/content-ref-data';
import { escapeSearchSpecialCharacters } from '@ilteducation/content-search/src/query-formatters';
import { useDialogFocus } from '@ilteducation/components';
import { BookWithDuplicates } from '@ilteducation/books';

type Props = {
  contentIds: string[];
  contextKey: string;
  gridProps?: {
    cols?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'bookClicked', bookId: string): void;
}>();

const contentIds = computed(() => props.contentIds);
const contextKey = computed(() => `${props.contextKey}-books`);
const { relatedContents, isLoading } = useSimilarContentContext<BookV1>(contextKey, contentIds, 'BookPart');

const searchContext = useSearchContexts<BookV1>('Book');
const { search } = searchContext;
const { context, isLoading: isLoadingLMGs } = storeToRefs(searchContext);

const selectedBook = ref<BookV1>();
const previewDialog = ref(false);
const { rememberFocus } = useDialogFocus([previewDialog]);

const handleBookSelection = (book: BookWithDuplicates) => {
  emit('bookClicked', book.id);
  rememberFocus(); //Capture focus of the active element
  selectedBook.value = book;
  previewDialog.value = true;
};

const stackRelatedBookParts = (contents: RelatedContent<BookV1>[]) => {
  const bookPartsByBookId = new Map<string, RelatedContent<BookV1> & { items: RelatedContentItem[] }>();
  contents.forEach((content) => {
    if (bookPartsByBookId.has(content.id)) {
      bookPartsByBookId.get(content.id)?.items.push(content.item);
    } else {
      bookPartsByBookId.set(content.id, { ...content, items: [content.item] });
    }
  });
  return Array.from(bookPartsByBookId.values());
};

const LMGsearchQuery = (book: BookV1) => ({
  filter: {
    originalTitle: escapeSearchSpecialCharacters(book.title),
  },
});

watch(relatedContents, (rc) => {
  Array.from(new Set(rc.map((content) => content.title)).values()).forEach((title) => {
    search({ filter: { originalTitle: escapeSearchSpecialCharacters(title) } }, 30);
  });
});

const getLMGsForBook = (book: BookV1) => {
  return ((context.value(createSearchId('Book', LMGsearchQuery(book)))?.contents || []) as BookV1[]).filter(
    (b) => b.id !== book.id,
  );
};

const allBooksWithItems = computed(() => {
  return stackRelatedBookParts(relatedContents?.value).flatMap((book) => [
    book,
    ...(getLMGsForBook(book).map((b) => ({
      ...b,
      items: book.items,
    })) || []),
  ]);
});

const { categories } = useCategories();
const route = useRoute();
const subjectFromRoute = computed(() => categories.value.find((c) => c.slug === route.params?.subject));
const sortBySubjects = (b: BookV1, a: BookV1) => {
  if (!subjectFromRoute.value) {
    return 0;
  }
  if (b.categories?.includes(subjectFromRoute.value.id) && !a.categories?.includes(subjectFromRoute.value.id)) {
    return -1;
  }
  return 0;
};

const hasRelatedContents = computed(() => relatedContents.value.length > 0);
defineExpose({
  hasRelatedContents,
});

const gridProps = computed(() => {
  return (
    props.gridProps || {
      cols: 6,
      sm: 3,
    }
  );
});
</script>

<template>
  <v-row v-if="!isLoading" class="px-2 pb-6 pt-4">
    <v-col
      v-for="book in stackRelatedBookParts(relatedContents).toSorted(sortBySubjects)"
      :key="book.id"
      v-bind="gridProps"
      class="pa-4"
    >
      <BookCover
        :show-as-stack="!!context(createSearchId('Book', LMGsearchQuery(book)))?.contents?.length"
        class="cursor-pointer"
        :book="book"
        @on-book-click="handleBookSelection"
      ></BookCover>
    </v-col>
    <BookPreview
      v-if="selectedBook && !isLoadingLMGs(createSearchId('Book', LMGsearchQuery(selectedBook)))"
      v-model="previewDialog"
      :books="
        allBooksWithItems.filter(
          (bwi) => bwi.originalTitle === selectedBook?.title || bwi.title === selectedBook?.title,
        )
      "
    ></BookPreview>
  </v-row>
  <v-row v-else>
    <v-col v-for="index in 4" :key="index">
      <v-skeleton-loader height="250px" type="ossein"></v-skeleton-loader>
    </v-col>
  </v-row>
</template>

<style lang="scss" scoped></style>
